import type { DiscoveredChain } from '../core/chain/types';
import { leafIndexFromExtensions } from '../core/rfc6962/sct';
import { parseCertificate } from '../core/x509/parse';
import { getClient } from './state/clients';
import { registry } from './state/logs';

/**
 * Pure-web chain discovery: if the certificate carries an SCT with a leaf_index (tiled logs), read
 * that entry from the log and take the chain the CA submitted with it. No third-party service.
 * (RFC 6962 SCTs cannot be located without the issuer's key hash, so they are skipped here.)
 */
export async function chainFromLogs(chain: Uint8Array[]): Promise<DiscoveredChain | undefined> {
  const leafDer = chain[0]!;
  const leaf = parseCertificate(leafDer);
  const reg = registry.value;
  let lastError: unknown;
  for (const sct of leaf.scts) {
    const idx = leafIndexFromExtensions(sct.extensions);
    const log = reg.byLogId(sct.logId);
    if (idx === undefined || !log) continue;
    try {
      const client = getClient(log);
      const [entry] = await client.getEntries(idx, 1);
      if (!entry) continue;
      const ders: Uint8Array[] = [];
      for (const ref of entry.chain) ders.push('der' in ref ? ref.der : await client.resolveIssuer!(ref.fingerprint));
      if (ders.length === 0) continue;
      const first = parseCertificate(ders[0]!);
      if (first.subject !== leaf.issuer) continue; // not this certificate's chain after all
      const out = [leafDer, ...ders];
      const last = parseCertificate(out[out.length - 1]!);
      return { chain: out, subjects: out.map((d) => parseCertificate(d).subject), endsAtRoot: last.subject === last.issuer, source: 'ct-log', detail: `${log.description} entry #${idx}` };
    } catch (e) {
      lastError = e;
    }
  }
  if (lastError) throw lastError;
  return undefined;
}
